import "@/index.css";
import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { installSwrRefresh } from "@/lib/swrRefresh";
import { installGetDedup } from "@/lib/dedupedGet";
import SwrUpdatingPill from "@/components/SwrUpdatingPill";

installSwrRefresh();
installGetDedup();
import { AuthProvider } from "@/contexts/AuthContext";
import { useAuth } from "@/contexts/AuthContext";
import AuthCallback from "@/pages/AuthCallback";
import GlobalBasicCalculator from "@/components/GlobalBasicCalculator";
import ErrorBoundary from "@/components/ErrorBoundary";
import LearnIndex from "@/pages/learn/LearnIndex";
import LearnWhatIsDrip from "@/pages/learn/LearnWhatIsDrip";
import LearnKingsAristocratsAchievers from "@/pages/learn/LearnKingsAristocratsAchievers";
import LearnJepiVsJepq from "@/pages/learn/LearnJepiVsJepq";
import LearnSchdVsVoo from "@/pages/learn/LearnSchdVsVoo";
import LearnSchdVsVym from "@/pages/learn/LearnSchdVsVym";
import LearnSchdVsDgro from "@/pages/learn/LearnSchdVsDgro";
import LearnNiitSurtax from "@/pages/learn/LearnNiitSurtax";
import LearnForeignOrdinarySharesTax from "@/pages/learn/LearnForeignOrdinarySharesTax";
import LearnCostBasisMethods from "@/pages/learn/LearnCostBasisMethods";
import LearnSplitsAndTaxes from "@/pages/learn/LearnSplitsAndTaxes";
import LearnGrahamPiotroskiDeepValue from "@/pages/learn/LearnGrahamPiotroskiDeepValue";
import LearnAnalystCoverageWhoWhy from "@/pages/learn/LearnAnalystCoverageWhoWhy";
import LearnAccountingAnomalies from "@/pages/learn/LearnAccountingAnomalies";
import Analytics from "@/components/Analytics";
import LearnCoveredCallEtfs from "@/pages/learn/LearnCoveredCallEtfs";
import LearnYieldOnCost from "@/pages/learn/LearnYieldOnCost";
import LearnReitsBdcsCefs from "@/pages/learn/LearnReitsBdcsCefs";
import LearnQualifiedDividends from "@/pages/learn/LearnQualifiedDividends";
import LearnDividendTraps from "@/pages/learn/LearnDividendTraps";
import LearnDripVsCash from "@/pages/learn/LearnDripVsCash";
import LearnFourPhases from "@/pages/learn/LearnFourPhases";
import LearnWhyDripRoth from "@/pages/learn/LearnWhyDripRoth";
import LearnBoringIsBeautiful from "@/pages/learn/LearnBoringIsBeautiful";
import LearnBuild5kMonthly from "@/pages/learn/LearnBuild5kMonthly";
import LearnMonthlyDividendCalendar from "@/pages/learn/LearnMonthlyDividendCalendar";
import LearnFirstTenTickers from "@/pages/learn/LearnFirstTenTickers";
import LearnDividendTriangle from "@/pages/learn/LearnDividendTriangle";
import LearnFiveNumbers from "@/pages/learn/LearnFiveNumbers";
import LearnMlpsExplained from "@/pages/learn/LearnMlpsExplained";
import LearnInternationalDividends from "@/pages/learn/LearnInternationalDividends";
import LearnMonthlyVsQuarterly from "@/pages/learn/LearnMonthlyVsQuarterly";
import LearnDailyDividendStocks from "@/pages/learn/LearnDailyDividendStocks";
import LearnMoneyMarketFunds from "@/pages/learn/LearnMoneyMarketFunds";
import LearnMutualFundTickers from "@/pages/learn/LearnMutualFundTickers";
import LearnHowSharesAreHeld from "@/pages/learn/LearnHowSharesAreHeld";
import LearnTreasuryVehiclePreferreds from "@/pages/learn/LearnTreasuryVehiclePreferreds";
import LearnAutocallableElns from "@/pages/learn/LearnAutocallableElns";
import LearnPreferredTickers from "@/pages/learn/LearnPreferredTickers";
import LearnPayoutRatio from "@/pages/learn/LearnPayoutRatio";
import LearnExDividendTimeline from "@/pages/learn/LearnExDividendTimeline";
import LearnDividendCutsDecade from "@/pages/learn/LearnDividendCutsDecade";
import LearnBondsVsDividends from "@/pages/learn/LearnBondsVsDividends";
import LearnSectorRotation from "@/pages/learn/LearnSectorRotation";
import LearnTaxLossHarvesting from "@/pages/learn/LearnTaxLossHarvesting";
import LearnDividendCalendarMonths from "@/pages/learn/LearnDividendCalendarMonths";
import Learn9PercentYieldSafe from "@/pages/learn/Learn9PercentYieldSafe";
import LearnPreferredStocks from "@/pages/learn/LearnPreferredStocks";
import LearnSpecialDividendsCagr from "@/pages/learn/LearnSpecialDividendsCagr";
import LearnExUsAristocrats from "@/pages/learn/LearnExUsAristocrats";
import LearnHedgedVsUnhedgedEtfs from "@/pages/learn/LearnHedgedVsUnhedgedEtfs";
import LearnEvaluatingDividendEtfs from "@/pages/learn/LearnEvaluatingDividendEtfs";
// Round 95y (Jun 2026) — five new SEO Learn guides
import LearnWhatIsADividend from "@/pages/learn/LearnWhatIsADividend";
import LearnDividendGrowthRate from "@/pages/learn/LearnDividendGrowthRate";
import LearnWhenToSell from "@/pages/learn/LearnWhenToSell";
import LearnHowToScreen from "@/pages/learn/LearnHowToScreen";
import LearnTrackInSpreadsheet from "@/pages/learn/LearnTrackInSpreadsheet";
// Round 97j (Feb 28, 2026) — Non-Dividend Stock Guides (free, no paywall)
import LearnAnatomyOfAStockBuyback from "@/pages/learn/LearnAnatomyOfAStockBuyback";
import LearnBeneishAltmanForensicSafety from "@/pages/learn/LearnBeneishAltmanForensicSafety";
import LearnCloEtfsNestingDoll from "@/pages/learn/LearnCloEtfsNestingDoll";
import LearnMinerviniSepa from "@/pages/learn/LearnMinerviniSepa";
import LearnOperatingMomentum from "@/pages/learn/LearnOperatingMomentum";
import LearnRuleOf40 from "@/pages/learn/LearnRuleOf40";
import LearnBuffettOneDollarTest from "@/pages/learn/LearnBuffettOneDollarTest";
import LearnFreeCashFlowGrowth from "@/pages/learn/LearnFreeCashFlowGrowth";
import LearnCapitalAppreciationPlaybook from "@/pages/learn/LearnCapitalAppreciationPlaybook";
import LearnInsideTheGrowthEngine from "@/pages/learn/LearnInsideTheGrowthEngine";
import LearnSpottingMicroCapGiants from "@/pages/learn/LearnSpottingMicroCapGiants";
import LearnLifecycleOfAStock from "@/pages/learn/LearnLifecycleOfAStock";
import LearnMomentumTradingNonDividend from "@/pages/learn/LearnMomentumTradingNonDividend";
import LearnDisruptiveInnovation from "@/pages/learn/LearnDisruptiveInnovation";
import LearnExitStrategyNonDividend from "@/pages/learn/LearnExitStrategyNonDividend";
import LearnCyclicalGrowthInvesting from "@/pages/learn/LearnCyclicalGrowthInvesting";
import LearnShortSqueezeDynamics from "@/pages/learn/LearnShortSqueezeDynamics";
import LearnInternationalGrowthFrontiers from "@/pages/learn/LearnInternationalGrowthFrontiers";
import LearnValueTrapsOfGrowthCollapses from "@/pages/learn/LearnValueTrapsOfGrowthCollapses";
import LearnFutureDividendKings from "@/pages/learn/LearnFutureDividendKings";
import LearnTaxEfficiencyNonDividend from "@/pages/learn/LearnTaxEfficiencyNonDividend";
import LearnFoundersVision from "@/pages/learn/LearnFoundersVision";
import LearnValuationNonDividendStocks from "@/pages/learn/LearnValuationNonDividendStocks";
import LearnHiddenGemsVsValueTraps from "@/pages/learn/LearnHiddenGemsVsValueTraps";
import LearnPreRevenueTechBiotech from "@/pages/learn/LearnPreRevenueTechBiotech";
import LearnVolatilitySurvivalNonDiv from "@/pages/learn/LearnVolatilitySurvivalNonDiv";
import LearnInstitutional13FTracking from "@/pages/learn/LearnInstitutional13FTracking";
import LearnDebtToGrowthRatio from "@/pages/learn/LearnDebtToGrowthRatio";
import LearnPortfolioConstructionNonDiv from "@/pages/learn/LearnPortfolioConstructionNonDiv";
import LearnSpacLifecycle from "@/pages/learn/LearnSpacLifecycle";
import LearnUnits from "@/pages/learn/LearnUnits";
import StockSeoPage from "@/pages/stock/StockSeoPage";
import Dashboard from "@/pages/Dashboard";
import Screener from "@/pages/Screener";
import WarrantScreener from "@/pages/WarrantScreener";
import FounderLedPage from "@/pages/FounderLedPage";
import IpoWatchPage from "@/pages/IpoWatchPage";import TierChallenge from "@/pages/TierChallenge";
import HeatmapPage from "@/pages/HeatmapPage";
import Rebalancer from "@/pages/Rebalancer";
import SectorView from "@/pages/SectorView";
import SharedPortfolio from "@/pages/SharedPortfolio";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Glossary from "@/pages/Glossary";
import Landing1 from "@/pages/landing/Landing1";
import Landing2 from "@/pages/landing/Landing2";
import Landing3 from "@/pages/landing/Landing3";
import Landing4 from "@/pages/landing/Landing4";
import Landing5 from "@/pages/landing/Landing5";
import IsDividendStock from "@/pages/IsDividendStock";
import ExDividendDate from "@/pages/ExDividendDate";
import DividendReinvestmentCalculator from "@/pages/DividendReinvestmentCalculator";
import CoveredCallHub from "@/pages/CoveredCallHub";
import CoveredCallVersus from "@/pages/CoveredCallVersus";
import SectorIncomePage from "@/pages/SectorIncomePage";
import SectorIncomeIndex from "@/pages/SectorIncomeIndex";
import ForeignDividendStocks from "@/pages/ForeignDividendStocks";
import MoneyMarketFunds from "@/pages/MoneyMarketFunds";
import CanadianDividendStocks from "@/pages/CanadianDividendStocks";

// Sep 2026 — sector income SEO pages. Slugs must match
// services/sector_taxonomy.py PAGES keys.
const SECTOR_PAGE_SLUGS = [
  "dividend-paying-tech-stocks",
  "dividend-paying-utility-stocks",
  "dividend-paying-real-estate-stocks",
  "dividend-paying-energy-stocks",
  "dividend-paying-healthcare-stocks",
  "dividend-paying-industrial-stocks",
  "dividend-paying-consumer-staples-stocks",
  "dividend-paying-consumer-cyclical-stocks",
  "dividend-paying-materials-stocks",
  "dividend-paying-telecom-stocks",
  "dividend-paying-bank-stocks",
  "dividend-paying-insurance-stocks",
  "dividend-paying-asset-managers",
  "dividend-paying-specialty-finance-stocks",
  // Sep 2026 — industry sub-pages (slugs must match
  // services/sector_taxonomy.py INDUSTRY_PAGES keys).
  "dividend-paying-reits",
  "dividend-paying-regional-banks",
  "dividend-paying-national-banks",
  "dividend-paying-asset-management-stocks",
  "dividend-paying-savings-institutions",
  "dividend-paying-property-casualty-insurers",
  "dividend-paying-broker-dealers",
  "dividend-paying-oil-and-gas-stocks",
  "dividend-paying-electric-utilities",
  "dividend-paying-diversified-utilities",
  "dividend-paying-semiconductor-stocks",
  "dividend-paying-pharmaceutical-stocks",
  "dividend-paying-restaurant-stocks",
  "dividend-paying-shipping-stocks",
];
import HowTo from "@/pages/HowTo";
import CefStrategies from "@/pages/CefStrategies";
import WhatIf from "@/pages/WhatIf";
import News from "@/pages/News";
import MegaTier from "@/pages/MegaTier";
import TrueReturn from "@/pages/TrueReturn";
import AccountingAnomaliesHub from "@/pages/AccountingAnomaliesHub";
import BuffettDollarTest from "@/pages/BuffettDollarTest";
import TaxLots from "@/pages/TaxLots";
import TaxHarvestReport from "@/pages/TaxHarvestReport";
import YearEndReport from "@/pages/YearEndReport";
import TaxAccountAudit from "@/pages/TaxAccountAudit";
import Journal from "@/pages/Journal";
import JournalTag from "@/pages/JournalTag";
import CommunityTerms from "@/pages/CommunityTerms";
import Community from "@/pages/Community";
import AdminCommunity from "@/pages/AdminCommunity";
import CommunityProfile from "@/pages/CommunityProfile";
import TriangleStandalone from "@/pages/TriangleStandalone";
import TriangleCompare from "@/pages/TriangleCompare";
import MedTaken from "@/pages/MedTaken";
import AppHeader from "@/components/AppHeader";
import useVersionWatcher from "@/hooks/useVersionWatcher";
import { COMMUNITY_ENABLED } from "@/config/featureFlags";

// Round 91l (Feb 2026) — Root route gating for SEO.
// twylightcrow.com/ used to render the Dashboard, which is a
// data-heavy app with very little crawlable text. Google + Bing
// were indexing the meta tags but ranking the site near-zero
// because the body content was empty until JS ran. This wrapper
// now serves the Landing1 marketing page (rich text, feature
// lists, live tape, MWF cards) to UNAUTHENTICATED visitors —
// including crawlers and share-link cold opens — and the real
// Dashboard to signed-in users so the app experience is
// unchanged for returning users.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    // While auth state is hydrating, render the marketing page —
    // it's the SEO-safe default and never wrong for crawlers.
    return <Landing1 />;
  }
  return user ? <Dashboard /> : <Landing1 />;
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppRouter() {
  const location = useLocation();
  // Poll /api/version every 3 min + on tab focus. When the server's
  // build_stamp differs from the one we saw on first load, show a
  // "New version available · Reload" toast so users don't stay stuck
  // on stale bundles after a deploy.
  useVersionWatcher();
  // useBackgroundTrackedRefresh (removed 2026-05-02): previously ran on
  // every mount — including accidental tab re-mounts — and silently
  // fired a portfolio+watchlist bulk refresh (up to 2,000 tickers) which
  // cascaded into per-ticker legacy Finimpulse enrichment. Disabled per user
  // request because a single unattended run was costing $45+ in API
  // credits. Per-stock refresh on the portfolio page remains.
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  // Round 116 (Feb 2026) — /med is a private, off-site page (Google
  // login only, allowlisted email + admin). It renders standalone
  // without the DiviDrip site header/nav.
  const isMedRoute =
    location.pathname === '/med' || location.pathname === '/med/';
  return (
    <>
      <ScrollToTop />
      {!isMedRoute && <AppHeader />}
      <Routes>
        <Route path="/med" element={<MedTaken />} />
        <Route path="/screener" element={<Screener />} />
        <Route path="/mega-tier" element={<MegaTier />} />
        <Route path="/true-return" element={<TrueReturn />} />
        <Route path="/true-return/:combo" element={<TrueReturn />} />
        <Route path="/accounting-anomalies" element={<AccountingAnomaliesHub />} />
        <Route path="/buffett-test" element={<BuffettDollarTest />} />
        <Route path="/warrants" element={<WarrantScreener />} />
        <Route path="/founder-led" element={<FounderLedPage />} />
        <Route path="/ipo-watch" element={<IpoWatchPage />} />
        <Route path="/rebalancer" element={<Rebalancer />} />
        <Route path="/sector/:sectorName" element={<SectorView />} />
        <Route path="/kings-challenge" element={<TierChallenge tier="king" />} />
        <Route path="/aristocrats-challenge" element={<TierChallenge tier="aristocrat" />} />
        <Route path="/achievers-challenge" element={<TierChallenge tier="achiever" />} />
        <Route path="/kings-heatmap" element={<HeatmapPage tier="king" />} />
        <Route path="/aristocrats-heatmap" element={<HeatmapPage tier="aristocrat" />} />
        <Route path="/achievers-heatmap" element={<HeatmapPage tier="achiever" />} />
        <Route path="/p/:slug" element={<SharedPortfolio />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {COMMUNITY_ENABLED && <Route path="/community/terms" element={<CommunityTerms />} />}
        {COMMUNITY_ENABLED && <Route path="/community" element={<Community />} />}
        {COMMUNITY_ENABLED && <Route path="/community/profile/:handle" element={<CommunityProfile />} />}
        {COMMUNITY_ENABLED && <Route path="/admin/community" element={<AdminCommunity />} />}
        <Route path="/triangle/compare" element={<TriangleCompare />} />
        <Route path="/triangle/:ticker" element={<TriangleStandalone />} />
        <Route path="/glossary" element={<Glossary />} />
        <Route path="/glossary/cef-strategies" element={<CefStrategies />} />
        <Route path="/whatif" element={<WhatIf />} />
        <Route path="/news" element={<News />} />
        <Route path="/tax-lots" element={<TaxLots />} />
        <Route path="/tax-harvest-report" element={<TaxHarvestReport />} />
        <Route path="/year-end-report" element={<YearEndReport />} />
        <Route path="/tax-account-audit" element={<TaxAccountAudit />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/journal/tag/:tag" element={<JournalTag />} />
        {/* Round 90 (Feb 2026) — landing page concepts. Five distinct
            visual directions so the user can mix-and-match sections
            into the final shipped /start page. */}
        <Route path="/landing/1" element={<Landing1 />} />
        <Route path="/landing/2" element={<Landing2 />} />
        <Route path="/landing/3" element={<Landing3 />} />
        <Route path="/landing/4" element={<Landing4 />} />
        <Route path="/landing/5" element={<Landing5 />} />
        {/* Feb 2026 — programmatic-SEO landing per ticker.
            Route matches lowercase or uppercase ticker; the page
            normalizes internally and emits a lowercase canonical. */}
        <Route path="/is/:ticker/a-dividend-stock" element={<IsDividendStock />} />
        <Route path="/ticker/:ticker/ex-dividend-date" element={<ExDividendDate />} />
        <Route path="/dividend-reinvestment-calculator" element={<DividendReinvestmentCalculator />} />
        {/* Sep 2026 — covered-call / option-income ETF hub + head-to-head
            pages. `/:pair` only renders for `x-vs-y` slugs; anything else
            bounces to the root inside the component. */}
        <Route path="/covered-call-etfs" element={<CoveredCallHub />} />
        <Route path="/dividend-stocks-by-sector" element={<SectorIncomeIndex />} />
        <Route path="/foreign-dividend-stocks" element={<ForeignDividendStocks />} />
        <Route path="/money-market-fund-yields" element={<MoneyMarketFunds />} />
        <Route path="/canadian-dividend-stocks" element={<CanadianDividendStocks />} />
        {SECTOR_PAGE_SLUGS.map((s) => (
          <Route key={s} path={`/${s}`} element={<SectorIncomePage />} />
        ))}
        <Route path="/:pair" element={<CoveredCallVersus />} />
        <Route path="/how-to" element={<HowTo />} />
        <Route path="/howto" element={<HowTo />} />
        <Route path="/help" element={<HowTo />} />
        {/* Round 91m — keyword-rich SEO learning pages */}
        <Route path="/learn" element={<LearnIndex />} />
        <Route path="/learn/what-is-drip" element={<LearnWhatIsDrip />} />
        <Route path="/learn/dividend-kings-vs-aristocrats-vs-achievers" element={<LearnKingsAristocratsAchievers />} />
        <Route path="/learn/jepi-vs-jepq" element={<LearnJepiVsJepq />} />
        <Route path="/learn/schd-vs-voo" element={<LearnSchdVsVoo />} />
        <Route path="/learn/schd-vs-vym" element={<LearnSchdVsVym />} />
        <Route path="/learn/schd-vs-dgro" element={<LearnSchdVsDgro />} />
        <Route path="/learn/niit-surtax" element={<LearnNiitSurtax />} />
        <Route path="/learn/foreign-ordinary-shares-tax" element={<LearnForeignOrdinarySharesTax />} />
        <Route path="/learn/cost-basis-methods-fifo-lifo-hifo" element={<LearnCostBasisMethods />} />
        <Route path="/learn/splits-and-taxes" element={<LearnSplitsAndTaxes />} />
        <Route path="/learn/graham-piotroski-deep-value" element={<LearnGrahamPiotroskiDeepValue />} />
        <Route path="/learn/analyst-coverage-who-why" element={<LearnAnalystCoverageWhoWhy />} />
        <Route path="/learn/accounting-anomalies-enron-checklist" element={<LearnAccountingAnomalies />} />
        <Route path="/learn/covered-call-etfs" element={<LearnCoveredCallEtfs />} />
        {/* Round 91u — second batch of 5 SEO Learn pages */}
        <Route path="/learn/yield-on-cost" element={<LearnYieldOnCost />} />
        <Route path="/learn/reits-vs-bdcs-vs-cefs" element={<LearnReitsBdcsCefs />} />
        <Route path="/learn/qualified-vs-ordinary-dividends" element={<LearnQualifiedDividends />} />
        <Route path="/learn/dividend-traps" element={<LearnDividendTraps />} />
        <Route path="/learn/drip-vs-cash-dividends" element={<LearnDripVsCash />} />
        {/* Round 92 — third batch of 10 SEO Learn pages */}
        <Route path="/learn/four-phases" element={<LearnFourPhases />} />
        <Route path="/learn/why-drip-roth" element={<LearnWhyDripRoth />} />
        <Route path="/learn/boring-is-beautiful" element={<LearnBoringIsBeautiful />} />
        <Route path="/learn/build-5k-monthly-income" element={<LearnBuild5kMonthly />} />
        <Route path="/learn/monthly-dividend-calendar" element={<LearnMonthlyDividendCalendar />} />
        <Route path="/learn/first-10-tickers" element={<LearnFirstTenTickers />} />
        <Route path="/learn/dividend-triangle" element={<LearnDividendTriangle />} />
        <Route path="/learn/five-numbers-to-check" element={<LearnFiveNumbers />} />
        <Route path="/learn/mlps-explained" element={<LearnMlpsExplained />} />
        <Route path="/learn/international-dividends" element={<LearnInternationalDividends />} />
        {/* Round 93 — two more SEO Learn pages */}
        <Route path="/learn/monthly-vs-quarterly-dividends" element={<LearnMonthlyVsQuarterly />} />
        <Route path="/learn/daily-dividend-stocks" element={<LearnDailyDividendStocks />} />
        <Route path="/learn/money-market-funds" element={<LearnMoneyMarketFunds />} />
        <Route path="/learn/mutual-fund-tickers" element={<LearnMutualFundTickers />} />
        <Route path="/learn/how-your-shares-are-held" element={<LearnHowSharesAreHeld />} />
        <Route path="/learn/treasury-vehicle-preferreds" element={<LearnTreasuryVehiclePreferreds />} />
        <Route path="/learn/autocallable-eln-etfs" element={<LearnAutocallableElns />} />
        <Route path="/learn/preferred-stock-tickers" element={<LearnPreferredTickers />} />
        <Route path="/learn/spac-lifecycle" element={<LearnSpacLifecycle />} />
        <Route path="/learn/units" element={<LearnUnits />} />
        <Route path="/learn/payout-ratio" element={<LearnPayoutRatio />} />
        <Route path="/learn/ex-dividend-date-timeline" element={<LearnExDividendTimeline />} />
        <Route path="/learn/dividend-cuts-of-the-decade" element={<LearnDividendCutsDecade />} />
        <Route path="/learn/bond-yields-vs-dividend-yields" element={<LearnBondsVsDividends />} />
        <Route path="/learn/sector-rotation-for-dividend-investors" element={<LearnSectorRotation />} />
        <Route path="/learn/tax-loss-harvesting-playbook" element={<LearnTaxLossHarvesting />} />
        <Route path="/learn/dividend-calendar-months" element={<LearnDividendCalendarMonths />} />
        <Route path="/learn/is-9-percent-yield-safe" element={<Learn9PercentYieldSafe />} />
        <Route path="/learn/preferred-stocks-explained" element={<LearnPreferredStocks />} />
        <Route path="/learn/special-dividends-in-cagr-math" element={<LearnSpecialDividendsCagr />} />
        <Route path="/learn/ex-us-dividend-aristocrats" element={<LearnExUsAristocrats />} />
        <Route path="/learn/hedged-vs-unhedged-etfs" element={<LearnHedgedVsUnhedgedEtfs />} />
        <Route path="/learn/evaluating-dividend-etfs" element={<LearnEvaluatingDividendEtfs />} />
        {/* Round 95y — five new SEO Learn guides */}
        <Route path="/learn/what-is-a-dividend" element={<LearnWhatIsADividend />} />
        <Route path="/learn/dividend-growth-rate" element={<LearnDividendGrowthRate />} />
        <Route path="/learn/when-to-sell-a-dividend-stock" element={<LearnWhenToSell />} />
        <Route path="/learn/how-to-screen-for-dividend-stocks" element={<LearnHowToScreen />} />
        <Route path="/learn/track-dividend-income-spreadsheet" element={<LearnTrackInSpreadsheet />} />
        {/* Round 97j (Feb 28, 2026) — Non-Dividend Stock Guides */}
        <Route path="/learn/anatomy-of-a-stock-buyback" element={<LearnAnatomyOfAStockBuyback />} />
        <Route path="/learn/beneish-altman-forensic-safety" element={<LearnBeneishAltmanForensicSafety />} />
        <Route path="/learn/clo-etfs-nesting-doll" element={<LearnCloEtfsNestingDoll />} />
        <Route path="/learn/minervini-sepa" element={<LearnMinerviniSepa />} />
        <Route path="/learn/operating-momentum" element={<LearnOperatingMomentum />} />
        <Route path="/learn/rule-of-40" element={<LearnRuleOf40 />} />
        <Route path="/learn/buffett-one-dollar-test" element={<LearnBuffettOneDollarTest />} />
        <Route path="/learn/free-cash-flow-growth" element={<LearnFreeCashFlowGrowth />} />
        <Route path="/learn/capital-appreciation-playbook" element={<LearnCapitalAppreciationPlaybook />} />
        <Route path="/learn/inside-the-growth-engine" element={<LearnInsideTheGrowthEngine />} />
        <Route path="/learn/spotting-micro-cap-giants" element={<LearnSpottingMicroCapGiants />} />
        <Route path="/learn/lifecycle-of-a-stock" element={<LearnLifecycleOfAStock />} />
        <Route path="/learn/momentum-trading-non-dividend" element={<LearnMomentumTradingNonDividend />} />
        <Route path="/learn/disruptive-innovation" element={<LearnDisruptiveInnovation />} />
        <Route path="/learn/exit-strategy-non-dividend" element={<LearnExitStrategyNonDividend />} />
        <Route path="/learn/cyclical-growth-investing" element={<LearnCyclicalGrowthInvesting />} />
        <Route path="/learn/short-squeeze-dynamics" element={<LearnShortSqueezeDynamics />} />
        <Route path="/learn/international-growth-frontiers" element={<LearnInternationalGrowthFrontiers />} />
        <Route path="/learn/value-traps-of-growth-collapses" element={<LearnValueTrapsOfGrowthCollapses />} />
        <Route path="/learn/future-dividend-kings" element={<LearnFutureDividendKings />} />
        <Route path="/learn/tax-efficiency-non-dividend" element={<LearnTaxEfficiencyNonDividend />} />
        <Route path="/learn/founders-vision-execution" element={<LearnFoundersVision />} />
        <Route path="/learn/valuation-non-dividend-stocks" element={<LearnValuationNonDividendStocks />} />
        <Route path="/learn/hidden-gems-vs-value-traps" element={<LearnHiddenGemsVsValueTraps />} />
        <Route path="/learn/pre-revenue-tech-biotech" element={<LearnPreRevenueTechBiotech />} />
        <Route path="/learn/volatility-survival-non-div" element={<LearnVolatilitySurvivalNonDiv />} />
        <Route path="/learn/institutional-13f-tracking" element={<LearnInstitutional13FTracking />} />
        <Route path="/learn/debt-to-growth-ratio" element={<LearnDebtToGrowthRatio />} />
        <Route path="/learn/portfolio-construction-non-div" element={<LearnPortfolioConstructionNonDiv />} />
        {/* Round 91n — per-stock SEO pages for long-tail ticker search traffic */}
        <Route path="/stock/:ticker" element={<StockSeoPage />} />
        {/* Round 91o — explicit /dashboard so logged-out visitors can leave Landing1 and reach the free Dashboard */}
        <Route path="/dashboard" element={<Dashboard />} />
        {/* Round 91l — explicit root route shows Landing1 for logged-out
            visitors (SEO + cold-traffic crawlers) and Dashboard for
            logged-in users. */}
        <Route path="/" element={<RootRoute />} />
        <Route path="*" element={<Dashboard />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    // Round 97a (Feb 2026) — Top-level ErrorBoundary so a render
    // error in ANY component (e.g. the Non-Dividend Quality Scorer
    // modal that black-screened production) can no longer wipe out
    // the entire React tree. Worst case the user sees a small
    // recovery card with a Reload button instead of staring at a
    // black page wondering what happened.
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <Analytics />
          <AppRouter />
          {/* Round 95bs — global Calculator listener mounted ONCE so
              the StockModal "Calculator" link works from every page,
              not just the Dashboard. */}
          <GlobalBasicCalculator />
          <SwrUpdatingPill />
          <Toaster
            position="bottom-center"
            richColors
            closeButton
            toastOptions={{ duration: 3500 }}
            // pointerEvents:auto — Radix Dialogs set body{pointer-events:none}
            // while open, which made the post-buy "Journal it" toast
            // unclickable from the Quick Stock Add modal (Sep 2026).
            style={{ zIndex: 10000, pointerEvents: 'auto' }}
          />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
